import type dayjs from 'dayjs';

/**
 * Converts a time string or dayjs object to a short, consistent format
 * Examples: "now", "21h.", "4d.", "3w.", "2mo.", "1y."
 *
 * Designed to work with dayjs().fromNow(true) output
 */
export function normalizeTime(time: string | dayjs.Dayjs): string {
  const timeString = typeof time === 'string' ? time : time.toString();

  // Handle seconds specially
  if (/a few seconds?|seconds?/i.test(timeString)) return 'now';

  // Handle singular forms
  if (/a minute/i.test(timeString)) return '1m.';
  if (/an hour/i.test(timeString)) return '1h.';
  if (/a day/i.test(timeString)) return '1d.';
  if (/a month/i.test(timeString)) return '1mo.';
  if (/a year/i.test(timeString)) return '1y.';

  // Handle plural forms with numbers
  const minuteMatch = timeString.match(/(\d+) minutes?/i);
  if (minuteMatch) return `${minuteMatch[1]}m.`;

  const hourMatch = timeString.match(/(\d+) hours?/i);
  if (hourMatch) return `${hourMatch[1]}h.`;

  const dayMatch = timeString.match(/(\d+) days?/i);
  if (dayMatch) {
    const days = parseInt(dayMatch[1]);
    // Convert to weeks if >= 14 days
    if (days >= 14) {
      const weeks = Math.floor(days / 7);
      return `${weeks}w.`;
    }
    return `${days}d.`;
  }

  const weekMatch = timeString.match(/(\d+) weeks?/i);
  if (weekMatch) return `${weekMatch[1]}w.`;

  const monthMatch = timeString.match(/(\d+) months?/i);
  if (monthMatch) return `${monthMatch[1]}mo.`;

  const yearMatch = timeString.match(/(\d+) years?/i);
  if (yearMatch) return `${yearMatch[1]}y.`;

  // Fallback - remove spaces
  return timeString.replaceAll(' ', '');
}
